import * as borogove from "borogove";
import { Paper } from "@mui/material";
import Draggable from "react-draggable";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useHotkeys } from "react-hotkeys-hook";
import { Button } from "@chatscope/chat-ui-kit-react";
import Dialpad from "@mui/icons-material/Dialpad";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPhone,
	faDesktop,
	faGripHorizontal,
	faGripVertical,
	fa0,
	fa1,
	fa2,
	fa3,
	fa4,
	fa5,
	fa6,
	fa7,
	fa8,
	fa9,
	faStarOfLife,
	faHashtag,
} from "@fortawesome/free-solid-svg-icons";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { href, useEffectEvent, readyServiceWorker } from "../util";
import { showCallNotification, useNotifications } from "../notifications";

const playTone = (frequency1: number, frequency2: number, duration: number) => {
	const audioContext = new AudioContext();
	const volume = 0.2;

	const gainNode = audioContext.createGain();
	gainNode.gain.setValueAtTime(volume, audioContext.currentTime);

	const oscillator1 = audioContext.createOscillator();
	oscillator1.type = "sine";
	oscillator1.frequency.setValueAtTime(frequency1, audioContext.currentTime);

	const oscillator2 = audioContext.createOscillator();
	oscillator2.type = "sine";
	oscillator2.frequency.setValueAtTime(frequency2, audioContext.currentTime);

	oscillator1.connect(gainNode);
	oscillator2.connect(gainNode);
	gainNode.connect(audioContext.destination);

	oscillator1.start();
	oscillator2.start();

	return {
		promise: new Promise<void>((resolve) => {
			setTimeout(async () => {
				oscillator1.stop();
				oscillator2.stop();
				try {
					await audioContext.close();
				} catch {}
				resolve();
			}, duration);
		}),
		stop: async () => {
			oscillator1.stop();
			oscillator2.stop();
			try {
				await audioContext.close();
			} catch {}
		},
	};
};

type DtmfDigit =
	| "1"
	| "2"
	| "3"
	| "4"
	| "5"
	| "6"
	| "7"
	| "8"
	| "9"
	| "0"
	| "*"
	| "#";

const playDtmf = (digit: DtmfDigit) => {
	const frequencies = {
		"1": [697, 1209],
		"2": [697, 1336],
		"3": [697, 1477],
		"4": [770, 1209],
		"5": [770, 1336],
		"6": [770, 1477],
		"7": [852, 1209],
		"8": [852, 1336],
		"9": [852, 1477],
		"*": [941, 1209],
		"0": [941, 1336],
		"#": [941, 1477],
	};

	return playTone(frequencies[digit][0], frequencies[digit][1], 500);
};

const playRingback = () => {
	return playTone(440, 480, 2000);
};

const playRingtone = () => {
	const audio = new Audio("/call.opus");
	return {
		promise: audio.play().then(
			() =>
				new Promise((resolve) => {
					audio.addEventListener("ended", resolve);
				}),
		),
		stop: () => audio.pause(),
	};
};

export const Call = ({
	accounts,
}: {
	accounts: Record<string, { client: borogove.Client }>;
}) => {
	const navigate = useNavigate();
	const { showNotification } = useNotifications();
	const [searchParams] = useSearchParams();

	const [showDtmf, setShowDtmf] = useState(false);
	const [call, setCall] = useState<borogove.calls.Session | null>(null);
	const [callAccountId, setCallAccountId] = useState<string | null>(null);
	const callChatId = call?.get_chatId();
	const callChat =
		callAccountId && callChatId
			? accounts[callAccountId]?.client?.getChat(callChatId)
			: null;
	const callStatus = call?.callStatus();

	const callAccountSelfChat = callAccountId
		? accounts[callAccountId]?.client?.getChat(callAccountId)
		: null;
	const callAccountIndicator = callAccountId ? (
		<div className="call-account" title={`Account: ${callAccountId}`}>
			<AvatarWithPlaceholder
				aria-hidden={true}
				size="xs"
				placeholderUri={callAccountSelfChat?.getPlaceholder()}
				photoUri={callAccountSelfChat?.getPhoto()}
			/>
			<span>for {callAccountSelfChat?.getDisplayName()}</span>
		</div>
	) : null;

	const getCallMedia = useEffectEvent(
		async (
			session: borogove.calls.InitiatedSession,
			audio: boolean,
			video: boolean,
		) => {
			if (session.get_sid() == call?.get_sid()) {
				const stream = await navigator.mediaDevices.getUserMedia({
					audio: audio,
					video: video,
				});
				session.supplyMedia([stream]);
			} else {
				console.warn(
					"A session that is not our current call asked for media",
					call,
					session,
				);
				session.supplyMedia([]);
			}
		},
	);

	const setCallEvent = useEffectEvent(
		(
			accountId: string,
			session: borogove.calls.Session | null,
			update: boolean,
		) => {
			if (update && call === null) {
				// Do not "update" a call that has ended
				return;
			}
			if (call && session && call.get_sid() !== session.get_sid()) {
				console.warn("We only support one call at a time", call, session);
				session?.hangup();
				return;
			}
			setCall(session ? new Proxy(session, {}) : null);
			setCallAccountId(accountId);
		},
	);

	useEffect(() => {
		const tokens = Object.fromEntries(
			Object.keys(accounts).map((accountId) => {
				const client = accounts[accountId].client;
				const set = (
					session: borogove.calls.Session | null,
					update?: boolean,
				) => setCallEvent(accountId, session, update ?? false);
				return [
					accountId,
					[
						client.addCallRingingListener(set),
						client.addCallRingListener(set),
						client.addCallUpdateStatusListener(set),
						client.addCallRetractListener(() => set(null)),
						client.addCallMediaListener(getCallMedia),
						client.addCallTrackListener((session, track) => {
							track.addEventListener("mute", () => set(session, true));
							track.addEventListener("unmute", () => set(session, true));
							track.addEventListener("ended", () => set(session, true));
							set(session, true);
						}),
					],
				];
			}),
		);

		return () => {
			for (const [accountId, ts] of Object.entries(tokens)) {
				ts.map((t) => accounts[accountId].client.removeEventListener(t));
			}
		};
	}, [accounts]);

	const notificationRef = useRef<Pick<Notification, "close"> | null>(null);
	const stopNowRef = useRef(() => {});
	const playingCancelledRef = useRef(false);
	const callStatusEvent = useEffectEvent(() => {
		let play = null;
		if (callStatus === borogove.calls.CallStatus.Outgoing) {
			playingCancelledRef.current = false;
			play = playRingback;
		}
		if (
			callStatus === borogove.calls.CallStatus.Incoming &&
			callAccountId &&
			callChat
		) {
			playingCancelledRef.current = false;
			play = playRingtone;
			(async () => {
				notificationRef.current = await showCallNotification(
					callAccountId,
					callChat,
					showNotification,
				);
			})();
		}

		if (!play) return;

		(async () => {
			while (!playingCancelledRef.current) {
				try {
					const { promise, stop } = play();
					stopNowRef.current = stop;
					await promise;
				} catch (e) {
					console.error(e);
				}
				await new Promise((resolve) => setTimeout(resolve, 2000));
			}
		})();
	});

	const callStatusCleanup = useEffectEvent(async () => {
		playingCancelledRef.current = true;
		stopNowRef.current();
		notificationRef.current?.close();
		const serviceWorker = await readyServiceWorker;
		if (!serviceWorker) return;
		const notifs = await serviceWorker.getNotifications({
			tag: callAccountId + ">" + call?.get_chatId() + ">call",
		});
		notifs.forEach((notif) => notif.close());
	});

	useEffect(() => {
		callStatusEvent();

		return () => {
			callStatusCleanup();
		};
	}, [callStatus]);

	const oneDtmf = (digit: DtmfDigit) => {
		playDtmf(digit);
		call?.dtmf()?.insertDTMF(digit, 500);
	};

	useHotkeys(
		["Mod+.", "Call"],
		() => call?.accept(),
		{
			preventDefault: true,
			enableOnContentEditable: true,
			enableOnFormTags: true,
			description: "Join call",
		},
		[call],
	);

	useHotkeys(
		["Mod+Comma", "EndCall"],
		() => call?.hangup(),
		{
			preventDefault: true,
			enableOnContentEditable: true,
			enableOnFormTags: true,
			description: "Hangup call",
		},
		[call],
	);

	return (
		<>
			{call?.audioTracks()?.map((track) => (
				<audio
					key={track.id}
					ref={(audio) => {
						if (audio) {
							audio.srcObject = new MediaStream([track]);
							audio.play();
						}
					}}
				></audio>
			))}

			{callStatus === borogove.calls.CallStatus.Ongoing && call && callChat && (
				<Draggable allowMobileScroll={true}>
					<div className="call-overlay">
						<Paper
							elevation={4}
							sx={{
								transform: "translateX(-50%)",
								border: "1px solid red",
								padding: "0.5em 1em",
								borderRadius: "1em",
							}}
						>
							{call.videoTracks().length > 0 &&
								call.videoTracks().map((track) => (
									<div className="embed-responsive" key={track.id}>
										<video
											ref={(video) => {
												if (video) {
													video.srcObject = new MediaStream([track]);
													video.play();
												}
											}}
										></video>
									</div>
								))}

							<div
								style={{
									display: showDtmf ? "block" : "none",
									textAlign: "center",
								}}
							>
								<div>
									<Button
										icon={<FontAwesomeIcon icon={fa1} />}
										onClick={() => oneDtmf("1")}
									/>
									<Button
										icon={<FontAwesomeIcon icon={fa2} />}
										onClick={() => oneDtmf("2")}
									/>
									<Button
										icon={<FontAwesomeIcon icon={fa3} />}
										onClick={() => oneDtmf("3")}
									/>
								</div>
								<div>
									<Button
										icon={<FontAwesomeIcon icon={fa4} />}
										onClick={() => oneDtmf("4")}
									/>
									<Button
										icon={<FontAwesomeIcon icon={fa5} />}
										onClick={() => oneDtmf("5")}
									/>
									<Button
										icon={<FontAwesomeIcon icon={fa6} />}
										onClick={() => oneDtmf("6")}
									/>
								</div>
								<div>
									<Button
										icon={<FontAwesomeIcon icon={fa7} />}
										onClick={() => oneDtmf("7")}
									/>
									<Button
										icon={<FontAwesomeIcon icon={fa8} />}
										onClick={() => oneDtmf("8")}
									/>
									<Button
										icon={<FontAwesomeIcon icon={fa9} />}
										onClick={() => oneDtmf("9")}
									/>
								</div>
								<div>
									<Button
										icon={<FontAwesomeIcon icon={faStarOfLife} />}
										onClick={() => oneDtmf("*")}
									/>
									<Button
										icon={<FontAwesomeIcon icon={fa0} />}
										onClick={() => oneDtmf("0")}
									/>
									<Button
										icon={<FontAwesomeIcon icon={faHashtag} />}
										onClick={() => oneDtmf("#")}
									/>
								</div>
							</div>
							<FontAwesomeIcon
								icon={faGripVertical}
								style={{ marginRight: "1em" }}
							/>
							<AvatarWithPlaceholder
								className="call-participant"
								photoUri={callChat.getPhoto()}
								placeholderUri={callChat.getPlaceholder()}
								displayName={callChat.getDisplayName()}
								onClick={() =>
									navigate(
										href(
											"./chats/:chatId",
											{ chatId: call.get_chatId() },
											searchParams,
										),
									)
								}
							>
								<AvatarWithPlaceholder
									className="call-participant-account"
									size="xs"
									displayName={`Account: ${callAccountId}`}
									placeholderUri={callAccountSelfChat?.getPlaceholder()}
									photoUri={callAccountSelfChat?.getPhoto()}
								/>
							</AvatarWithPlaceholder>
							<span
								style={{
									display: "inline-block",
									width: "7em",
									maxWidth: "16vw",
									minWidth: "4em",
								}}
							>
								On call…
							</span>

							{call.dtmf() && (
								<Button
									icon={<Dialpad />}
									onClick={() => setShowDtmf(!showDtmf)}
									style={{ margin: 0 }}
								/>
							)}

							<Button
								icon={<FontAwesomeIcon icon={faDesktop} />}
								onClick={() =>
									navigator.mediaDevices
										.getDisplayMedia()
										.then((stream) => call?.addMedia([stream]))
								}
								style={{ margin: 0 }}
							/>

							<Button
								icon={<FontAwesomeIcon icon={faPhone} />}
								onClick={() => call.hangup()}
								style={{
									background: "red",
									borderRadius: "100%",
									color: "white",
									width: "2.3em",
									height: "2.3em",
									marginLeft: "0.75em",
								}}
							/>
						</Paper>
					</div>
				</Draggable>
			)}

			{callStatus &&
				call &&
				callChat &&
				[
					borogove.calls.CallStatus.Outgoing,
					borogove.calls.CallStatus.Connecting,
					borogove.calls.CallStatus.Failed,
				].includes(callStatus) && (
					<Draggable allowMobileScroll={true}>
						<div className="call-overlay">
							<Paper
								elevation={4}
								sx={{
									transform: "translateX(-50%)",
									padding: "1em 4em",
									borderRadius: "1em",
									textAlign: "center",
								}}
							>
								<FontAwesomeIcon
									icon={faGripHorizontal}
									style={{ marginBottom: "1em" }}
								/>

								<h2>
									{callStatus === borogove.calls.CallStatus.Outgoing &&
										"Calling…"}
									{callStatus === borogove.calls.CallStatus.Connecting &&
										"Connecting…"}
									{callStatus === borogove.calls.CallStatus.Failed &&
										"Failed to connect"}
								</h2>
								{callAccountIndicator}

								<div className="h-card">
									<AvatarWithPlaceholder
										className="u-photo"
										photoUri={callChat.getPhoto()}
										placeholderUri={callChat.getPlaceholder()}
										displayName={callChat.getDisplayName()}
										style={{ display: "inline-block", marginRight: "1em" }}
										onClick={() =>
											navigate(
												href(
													"./chats/:chatId",
													{ chatId: call.get_chatId() },
													searchParams,
												),
											)
										}
									/>
									{callChat.getDisplayName()}
								</div>

								<Button
									onClick={() => call.hangup()}
									style={{
										background: "red",
										borderRadius: "10em",
										color: "white",
										padding: "0.5em 2em",
										marginTop: "1.5em",
									}}
								>
									Cancel
								</Button>
							</Paper>
						</div>
					</Draggable>
				)}

			{callStatus === borogove.calls.CallStatus.Incoming &&
				call &&
				callChat && (
					<Draggable allowMobileScroll={true}>
						<div className="call-overlay">
							<Paper
								elevation={4}
								sx={{
									transform: "translateX(-50%)",
									padding: "1em 4em",
									borderRadius: "1em",
									textAlign: "center",
								}}
							>
								<FontAwesomeIcon
									icon={faGripHorizontal}
									style={{ marginBottom: "1em" }}
								/>

								<h2>Incoming call</h2>
								{callAccountIndicator}

								<div className="h-card">
									<AvatarWithPlaceholder
										className="u-photo"
										photoUri={callChat.getPhoto()}
										placeholderUri={callChat.getPlaceholder()}
										displayName={callChat.getDisplayName()}
										style={{ display: "inline-block", marginRight: "1em" }}
										onClick={() =>
											navigate(
												href(
													"./chats/:chatId",
													{ chatId: call.get_chatId() },
													searchParams,
												),
											)
										}
									/>
									{callChat.getDisplayName()}
								</div>

								<div style={{ marginTop: "1.5em" }}>
									<Button
										border
										onClick={() => call.accept()}
										style={{
											borderRadius: "10em",
											padding: "0.5em 2em",
										}}
									>
										Join Call
									</Button>
									<Button
										onClick={() => call.hangup()}
										style={{
											background: "red",
											borderRadius: "10em",
											color: "white",
											padding: "0.5em 2em",
										}}
									>
										Decline
									</Button>
								</div>
							</Paper>
						</div>
					</Draggable>
				)}
		</>
	);
};
